import React, { useEffect, useMemo, useRef, useState } from 'react';
import AuditLogTable, { ACTION_CONFIG } from '../Tables/AuditLogTable';
import { auditLogApi } from '../../../api/auditLogApi';

const ACTION_FILTER_OPTIONS = [
  { value: 'All', label: 'All actions' },
  ...Object.entries(ACTION_CONFIG).map(([value, config]) => ({
    value,
    label: config.label,
  })),
];

function parseError(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    'Unable to load audit logs from the backend.'
  );
}

function ControlField({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm font-medium text-gray-700">
      <span>{label}</span>
      {children}
    </label>
  );
}

function shortenValue(value, head = 8, tail = 4) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= head + tail + 1) return text;
  return `${text.slice(0, head)}...${text.slice(-tail)}`;
}

function BackendFilters({
  actionFilter,
  onActionChange,
  fromDate,
  onFromDateChange,
  toDate,
  onToDateChange,
  actorUuid,
  onActorUuidChange,
  targetUuid,
  onTargetUuidChange,
  onClear,
}) {
  const hasFilters =
    actionFilter !== 'All' ||
    Boolean(fromDate) ||
    Boolean(toDate) ||
    Boolean(String(actorUuid || '').trim()) ||
    Boolean(String(targetUuid || '').trim());

  return (
    <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">Filter audit log</p>
          <p className="text-xs text-gray-500">
            These values are sent to the API before the audit rows are loaded.
          </p>
        </div>
        <button
          type="button"
          onClick={onClear}
          disabled={!hasFilters}
          className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Clear filters
        </button>
      </div>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        <ControlField label="Action">
          <div className="relative">
            <select
              value={actionFilter}
              onChange={(event) => onActionChange(event.target.value)}
              className="w-full appearance-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 pr-9 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
            >
              {ACTION_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <svg
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </ControlField>

        <ControlField label="From">
          <input
            type="date"
            value={fromDate}
            onChange={(event) => onFromDateChange(event.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
          />
        </ControlField>

        <ControlField label="To">
          <input
            type="date"
            value={toDate}
            onChange={(event) => onToDateChange(event.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
          />
        </ControlField>
      </div>

      <details className="mt-4 rounded-xl border border-dashed border-gray-200 bg-gray-50/70 px-3 py-2">
        <summary className="cursor-pointer list-none text-sm font-medium text-gray-700">
          Advanced UUID filters
        </summary>
        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <ControlField label="Actor UUID">
            <input
              type="text"
              value={actorUuid}
              onChange={(event) => onActorUuidChange(event.target.value)}
              placeholder="008e9fc6-..."
              className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
            />
          </ControlField>

          <ControlField label="Target UUID">
            <input
              type="text"
              value={targetUuid}
              onChange={(event) => onTargetUuidChange(event.target.value)}
              placeholder="7c89b5f5-..."
              className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
            />
          </ControlField>
        </div>
      </details>
    </div>
  );
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const [actionFilter, setActionFilter] = useState('All');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [actorUuid, setActorUuid] = useState('');
  const [targetUuid, setTargetUuid] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const requestRef = useRef(0);

  // One page at a time, straight from the backend's own pagination — never
  // "fetch every page up front" (auditLogApi.listAll's fan-out), since the
  // audit log logs every action in the system and only ever grows.
  const backendParams = useMemo(() => {
    const params = { per_page: pageSize, page };
    if (actionFilter !== 'All') params.action = actionFilter;
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;
    if (String(actorUuid || '').trim()) params.actor_uuid = String(actorUuid).trim();
    if (String(targetUuid || '').trim()) params.target_uuid = String(targetUuid).trim();
    return params;
  }, [actionFilter, actorUuid, fromDate, page, pageSize, targetUuid, toDate]);

  const activeBackendFilters = useMemo(() => {
    const parts = [];
    if (actionFilter !== 'All') {
      const label = ACTION_CONFIG[actionFilter]?.label ?? actionFilter;
      parts.push(`Action: ${label}`);
    }
    if (fromDate) parts.push(`From: ${fromDate}`);
    if (toDate) parts.push(`To: ${toDate}`);
    if (String(actorUuid || '').trim()) parts.push(`Actor UUID: ${shortenValue(actorUuid)}`);
    if (String(targetUuid || '').trim()) parts.push(`Target UUID: ${shortenValue(targetUuid)}`);
    return parts;
  }, [actionFilter, actorUuid, fromDate, targetUuid, toDate]);

  useEffect(() => {
    let active = true;
    const requestId = ++requestRef.current;

    const load = async () => {
      if (fromDate && toDate && fromDate > toDate) {
        if (!active || requestId !== requestRef.current) return;
        setError('The From date cannot be later than the To date.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const data = await auditLogApi.list(backendParams);
        if (!active || requestId !== requestRef.current) return;
        setRows(Array.isArray(data?.items) ? data.items : []);
        setTotal(Number(data?.pagination?.total ?? 0));
      } catch (err) {
        if (!active || requestId !== requestRef.current) return;
        setError(parseError(err));
      } finally {
        if (active && requestId === requestRef.current) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [backendParams, fromDate, refreshTick, toDate]);

  const handleRetry = () => {
    setRefreshTick((value) => value + 1);
  };

  // Every filter change re-queries page 1 — the previous page number almost
  // never has meaning against a new filter's result set.
  const handleActionChange = (value) => { setActionFilter(value); setPage(1); };
  const handleFromDateChange = (value) => { setFromDate(value); setPage(1); };
  const handleToDateChange = (value) => { setToDate(value); setPage(1); };
  const handleActorUuidChange = (value) => { setActorUuid(value); setPage(1); };
  const handleTargetUuidChange = (value) => { setTargetUuid(value); setPage(1); };

  const handleClearFilters = () => {
    setActionFilter('All');
    setFromDate('');
    setToDate('');
    setActorUuid('');
    setTargetUuid('');
    setPage(1);
  };

  const handlePageSizeChange = (nextPageSize) => {
    setPageSize(nextPageSize);
    setPage(1);
  };

  const showTable = rows.length > 0 || (!loading && !error);

  return (
    <section>
      <BackendFilters
        actionFilter={actionFilter}
        onActionChange={handleActionChange}
        fromDate={fromDate}
        onFromDateChange={handleFromDateChange}
        toDate={toDate}
        onToDateChange={handleToDateChange}
        actorUuid={actorUuid}
        onActorUuidChange={handleActorUuidChange}
        targetUuid={targetUuid}
        onTargetUuidChange={handleTargetUuidChange}
        onClear={handleClearFilters}
      />

      {activeBackendFilters.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-2xl border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-800">
          <span className="font-semibold">Applied filters:</span>
          {activeBackendFilters.map((item) => (
            <span
              key={item}
              className="rounded-full border border-green-200 bg-white px-3 py-1 text-xs font-medium text-green-700"
            >
              {item}
            </span>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <span>{error}</span>
          <button
            type="button"
            onClick={handleRetry}
            className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      )}

      {loading && (
        <div className="mb-4 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">
          Loading audit log...
        </div>
      )}

      {showTable && (
        <AuditLogTable
          rows={rows}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </section>
  );
}
